import { Control, Controller, FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { BaseRadio, BaseTooltip } from "src/components";
import { BaseInputNumberFormat } from "src/components/BaseInputNumberFormat";
import { inputPriceFormat } from "src/utils";
import { FormData } from "./ChargeFrom";

interface IProps {
  control: Control<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  errors: FieldErrors<FormData>;
}

/**
 * 공간상품 등록/수정 > 요금/관리 > 충전금 관리 섹션 (RPP41 상품 크레딧 수정)
 * - 월별 기본 지급(monthlyCredit), 잔여 충전금 이월(isCreditCarryAllowed),
 *   초과 예약(isOverReservationAllowed), 후불 정산 한도(isPostpaidLimited / postpaidLimitAmount)
 */
const CreditManagementForm = ({ control, watch, setValue, errors }: IProps) => {
  const isOverReservationAllowed = watch("isOverReservationAllowed");
  const isPostpaidLimited = watch("isPostpaidLimited");

  return (
    <div className="contents-container__wrap-article">
      <div className="contents-container__sub-title">
        <div className="pb4">
          <h2>충전금 관리</h2>
          <ul className="base-list pt10">
            <li>유료 공용공간 예약시 이용 요금만큼 차감되는 결제수단입니다.</li>
            <li>매월 1일 정해진 충전금이 지급되며, 지급된 충전금은 당월 1일부터 말일까지 사용할 수 있습니다.</li>
          </ul>
        </div>
      </div>

      {/* 월별 기본 지급 */}
      <section className="contents-container__grid contents-container__1200">
        <div className="contents-container__grid-index">
          <p className="required">월별 기본 지급</p>
        </div>
        <div className="contents-container__grid-contents">
          <div className="flex-row flex-center-start no-wrap">
            <div className="minmax160">
              <Controller
                control={control}
                name="monthlyCredit"
                render={({ field: { onChange, value, name, ref } }) => (
                  <BaseInputNumberFormat inputRef={ref} type="text" onChange={onChange} value={inputPriceFormat(value)} name={name} placeholder="0" />
                )}
              />
            </div>
            <span className="ml10">원</span>
          </div>
          <ul className="base-list pt10">
            <li>미설정 시 이 상품으로 생성되는 계약의 월별 지급 충전금은 0원으로 적용됩니다.</li>
          </ul>
        </div>
      </section>

      {/* 잔여 충전금 이월 */}
      <section className="contents-container__grid contents-container__1200">
        <div className="contents-container__grid-index">
          <p className="required">잔여 충전금 이월</p>
        </div>
        <div className="contents-container__grid-contents">
          <Controller
            control={control}
            name="isCreditCarryAllowed"
            render={({ field }) => (
              <div className="flex-row flex-center-start">
                <BaseRadio
                  id="creditCarryDisallow"
                  name="isCreditCarryAllowed"
                  label="비허용"
                  checked={field.value === false}
                  onChange={() => setValue("isCreditCarryAllowed", false, { shouldValidate: true })}
                />
                <BaseTooltip className="ml2 mr16" contents="당월 말일 24시 기준으로 잔여 충전금을 소멸시킵니다." touchIcon="QUESTION" />
                <BaseRadio
                  id="creditCarryAllow"
                  name="isCreditCarryAllowed"
                  label="허용"
                  checked={field.value === true}
                  onChange={() => setValue("isCreditCarryAllowed", true, { shouldValidate: true })}
                />
                <BaseTooltip
                  className="ml2"
                  contents="당월 말일 24시 기준으로 잔여 충전금이 있을 경우, 익월 1일 00:05 ~ 00:10 사이에 익월 충전금으로 이월 지급합니다."
                  touchIcon="QUESTION"
                />
              </div>
            )}
          />
        </div>
      </section>

      {/* 초과 예약 */}
      <section className="contents-container__grid contents-container__1200">
        <div className="contents-container__grid-index">
          <p className="required">초과 예약</p>
        </div>
        <div className="contents-container__grid-contents">
          <Controller
            control={control}
            name="isOverReservationAllowed"
            render={({ field }) => (
              <div className="flex-row flex-center-start pb10">
                <BaseRadio
                  id="overReservationDisallow"
                  name="isOverReservationAllowed"
                  label="비허용"
                  checked={field.value === false}
                  onChange={() => setValue("isOverReservationAllowed", false, { shouldValidate: true })}
                  className="mr16"
                />
                <BaseRadio
                  id="overReservationAllow"
                  name="isOverReservationAllowed"
                  label="허용"
                  checked={field.value === true}
                  onChange={() => setValue("isOverReservationAllowed", true, { shouldValidate: true })}
                />
              </div>
            )}
          />

          {/* 후불 정산 한도 (초과 예약 허용 시에만 활성화) */}
          <div className="gray-box">
            <div className="flex-center no-wrap">
              <p className="required minmax140">후불 정산 한도</p>
              <Controller
                control={control}
                name="isPostpaidLimited"
                render={({ field }) => (
                  <div className="flex-center no-wrap">
                    <BaseRadio
                      id="postpaidUnlimited"
                      name="isPostpaidLimited"
                      label="무제한"
                      checked={field.value === false}
                      disabled={!isOverReservationAllowed}
                      onChange={() => setValue("isPostpaidLimited", false, { shouldValidate: true })}
                      className="mr16"
                    />
                    <BaseRadio
                      id="postpaidLimited"
                      name="isPostpaidLimited"
                      label="제한"
                      checked={field.value === true}
                      disabled={!isOverReservationAllowed}
                      onChange={() => setValue("isPostpaidLimited", true, { shouldValidate: true })}
                      className="mr16"
                    />
                  </div>
                )}
              />
              <div className="minmax160">
                <Controller
                  control={control}
                  name="postpaidLimitAmount"
                  render={({ field: { onChange, value, name, ref } }) => (
                    <BaseInputNumberFormat
                      inputRef={ref}
                      type="text"
                      onChange={onChange}
                      value={inputPriceFormat(value)}
                      name={name}
                      placeholder="0"
                      readonly={!isOverReservationAllowed || !isPostpaidLimited}
                    />
                  )}
                />
              </div>
              <span className="ml10">원</span>
            </div>
            {errors?.postpaidLimitAmount && <p className="validation-text">{errors?.postpaidLimitAmount?.message}</p>}
          </div>

          <ul className="base-list pt10">
            <li>잔여 충전금이 부족할 때 초과 예약 및 후불 정산 허용 여부를 설정합니다.</li>
            <li>초과 예약 허용 시 후불 정산 가능한 최대 금액을 설정할 수 있습니다.</li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default CreditManagementForm;
